import * as yup from 'yup';

/**
 * Validation schema for media URLs
 */
const mediaUrlsSchema = yup.object().shape({
  s3_audio_url: yup
    .string()
    .url('Audio URL must be a valid URL')
    .optional()
    .nullable(),
  s3_screenshot_url: yup
    .string()
    .url('Screenshot URL must be a valid URL')
    .optional()
    .nullable(),
});

/**
 * Main validation schema for scene editing
 */
export const sceneValidationSchema = yup.object().shape({
  title: yup
    .string()
    .required('Scene title is required')
    .trim()
    .min(1, 'Scene title cannot be empty')
    .max(150, 'Scene title cannot exceed 150 characters'),

  // Empty is allowed on purpose: clearing a slide back to blank is a valid edit
  // (the scene keeps its title, narration and media). The length floor applies
  // only once there IS content, so a half-typed slide is still caught.
  html_content: yup
    .string()
    .defined('Scene content is required')
    .test(
      'min-length-when-present',
      'Content must be at least 10 characters',
      (value) => !value || value.trim().length === 0 || value.length >= 10
    ),

  narration: yup
    .string()
    .trim()
    .max(2000, 'Narration cannot exceed 2000 characters')
    .optional()
    .nullable(),

  visual_description: yup
    .string()
    .trim()
    .max(500, 'Visual description cannot exceed 500 characters')
    .optional()
    .nullable(),

  media_urls: mediaUrlsSchema.optional().nullable(),
});

/**
 * Type-safe validation function for scenes
 */
export const validateScene = async (data: any) => {
  try {
    await sceneValidationSchema.validate(data, { abortEarly: false });
    return { valid: true, errors: {} };
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors: Record<string, string> = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      return { valid: false, errors };
    }
    return { valid: false, errors: { _form: 'Validation failed' } };
  }
};

/**
 * TypeScript interface for scene data
 */
export interface SceneFormData {
  title: string;
  html_content: string;
  narration?: string | null;
  visual_description?: string | null;
  media_urls?: {
    s3_audio_url?: string | null;
    s3_screenshot_url?: string | null;
  } | null;
}
